import { userApi } from '../redux/api/axiosInstance';

export type SetupReadiness = 'Unlicensed' | 'NotProvisioned' | 'RequiresConfiguration' | 'Operational';
export type SetupTaskSeverity = 'Required' | 'Recommended' | 'Informational';

export interface SetupTask {
  code: string;
  description: string;
  severity: SetupTaskSeverity;
}

export interface SetupStatus {
  providerCode: string;
  displayName: string;
  isLicensed: boolean;
  isProvisioned: boolean;
  isConfigured: boolean;
  readiness: SetupReadiness;
  pendingTasks: SetupTask[];
  completedTasks: SetupTask[];
  warnings: string[];
  errors: string[];
  lastChecked: string;
  additionalMetadata?: Record<string, any>;
}

export interface OrganizationSetupSummary {
  organizationId: number;
  organizationName: string | null;
  isFullyOperational: boolean;
  modules: SetupStatus[];
  evaluatedAt: string;
  duration: string;
}

export interface SubscriptionStatus {
  planCode: string;
  status: string;
  isTrial: boolean;
  trialEndDate: string | null;
  subscriptionEndDate: string | null;
  /** Whole days remaining in the trial (ceiling). null when not in trial. 0 = ends today. */
  trialExpiresInDays: number | null;
  /** True when the org is in a trial that has passed its end date. */
  isTrialExpired: boolean;
  /** Plan-derived feature codes active for this subscription.
   *  Use hasFeature("SALARY_REVISION") from AppContext instead of branching on planCode. */
  enabledFeatures: string[];
}

// ── Enum normalization ────────────────────────────────────────────────────────
// The backend enums (SetupReadiness, SetupTaskSeverity) are string-serialized, but
// older builds emit them as integer ordinals. Normalize to the canonical string
// form here so every consumer (AppContext, SetupCenter, SetupBanner, dashboards)
// can compare readiness === 'Operational' / severity === 'Required' reliably.
const READINESS_BY_ORDINAL: SetupReadiness[] = [
  'Unlicensed',            // 0
  'NotProvisioned',        // 1
  'RequiresConfiguration', // 2
  'Operational',           // 3
];
const SEVERITY_BY_ORDINAL: SetupTaskSeverity[] = [
  'Required',      // 0
  'Recommended',   // 1
  'Informational', // 2
];

const normalizeReadiness = (value: unknown): SetupReadiness =>
  typeof value === 'number' ? READINESS_BY_ORDINAL[value] ?? 'Unlicensed' : (value as SetupReadiness);

const normalizeSeverity = (value: unknown): SetupTaskSeverity =>
  typeof value === 'number' ? SEVERITY_BY_ORDINAL[value] ?? 'Required' : (value as SetupTaskSeverity);

const normalizeTasks = (tasks: SetupTask[] | undefined): SetupTask[] =>
  (tasks ?? []).map((t) => ({ ...t, severity: normalizeSeverity(t.severity) }));

const normalizeSummary = (summary: OrganizationSetupSummary): OrganizationSetupSummary => ({
  ...summary,
  modules: (summary.modules ?? []).map((m) => ({
    ...m,
    readiness: normalizeReadiness(m.readiness),
    pendingTasks: normalizeTasks(m.pendingTasks),
    completedTasks: normalizeTasks(m.completedTasks),
  })),
});

export const getSetupSummary = async (): Promise<OrganizationSetupSummary> => {
  const { data } = await userApi.get('setup/summary');
  return normalizeSummary(data);
};

export const refreshSetupCache = async (): Promise<OrganizationSetupSummary> => {
  const { data } = await userApi.post('setup/refresh');
  return normalizeSummary(data);
};

export const getModuleSetupStatus = async (providerCode: string): Promise<SetupStatus> => {
  const { data } = await userApi.get(`setup/modules/${providerCode}`);
  return data;
};

export const getActiveSubscription = async (): Promise<SubscriptionStatus> => {
  const { data } = await userApi.get('subscriptions/active');
  return data;
};

export const getProvisioningStatus = async (organizationId: number) => {
  const { data } = await userApi.get(`organization/${organizationId}/provision/status`);
  return data;
};

export const repairProvisioning = async (organizationId: number) => {
  const { data } = await userApi.post(`organization/${organizationId}/provision`);
  return data;
};

// HRMS-52: Setup Center starter-data packs (browse → install generic quick-start data).
export interface StarterPackItem { name: string; detail: string; installed: boolean; }
export interface StarterPack {
  code: string;
  domain: string;
  title: string;
  description: string;
  severity: 'Required' | 'Recommended';
  configureRoute: string;
  totalItems: number;
  installedItems: number;
  fullyInstalled: boolean;
  items: StarterPackItem[];
}
export interface StarterPackInstallResult {
  code: string;
  inserted: number;
  skipped: number;
  insertedItems: string[];
  skippedItems: string[];
}
export const getStarterPacks = async (): Promise<StarterPack[]> => {
  const { data } = await userApi.get('starter-packs');
  return data;
};
// HRMS-60/61: optional user-edited overrides. Omit for default install.
export interface StarterPackShiftInput { name?: string; startTime?: string; endTime?: string; graceMinutes?: number; }
export interface StarterPackInstallBody { names?: string[]; shift?: StarterPackShiftInput; }
export const installStarterPack = async (code: string, body?: StarterPackInstallBody): Promise<StarterPackInstallResult> => {
  const { data } = await userApi.post(`starter-packs/${code}/install`, body ?? {});
  return data;
};
